import { useEffect, useRef } from "react";
import {
  IconGift,
  IconCalendarEvent,
  IconClock24,
  IconShieldLock,
  IconSparkles,
} from "@tabler/icons-react";
import styles from "./GlobalTrustPoints.module.css";

const POINTS = [
  {
    icon: <IconGift size={24} />,
    title: "First chat free",
    // Was "no card, no sign-up wall". The card part is true; the sign-up part
    // was not. guestChatEnabled in hooks/useGuestChat.ts requires `isForeign`,
    // so only non-Indian visitors reach /chat without an account, and even
    // then it is a teaser. India is 91.5% of our traffic, so the claim was
    // false for almost everyone who read it.
    text: "Ask your first question free. No card required.",
  },
  {
    icon: <IconCalendarEvent size={24} />,
    title: "100% personal",
    text: "Read from your exact birth date, time, and place.",
  },
  {
    icon: <IconClock24 size={24} />,
    title: "Available 24/7",
    text: "Answers any hour, in your own language.",
  },
  {
    icon: <IconShieldLock size={24} />,
    title: "Private & secure",
    text: "Your details are encrypted and never sold.",
  },
  {
    icon: <IconSparkles size={24} />,
    title: "Real Vedic AI",
    text: "Classical Jyotish, read by AI and explained simply.",
  },
];


const N = POINTS.length;

/* ===== Full-bleed left-to-right coverflow, curved at the ends (desktop + mobile) ===== */
const SPACING = 196;
const COPIES = 3;
const MAX_ANGLE = 52;
const MAX_Z = 230;

function CoverflowTrust() {
  const stageRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const offset = useRef(0);
  const paused = useRef(false);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startOffset = useRef(0);

  const slots = COPIES * N; // tiled across the full width for a seamless loop

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    let raf = 0;
    let running = false;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const total = slots * SPACING;

    const layout = () => {
      const halfW = (stageRef.current?.clientWidth ?? 1280) / 2;
      cardRefs.current.forEach((el, i) => {
        if (!el) return;
        let pos = i * SPACING - offset.current;
        pos = ((pos % total) + total) % total;
        if (pos > total / 2) pos -= total;
        const r = Math.max(-1, Math.min(1, pos / halfW));
        const ry = -r * MAX_ANGLE;
        const z = -Math.abs(r) * MAX_Z;
        const sc = 1 - Math.abs(r) * 0.12;
        // only write transform/zIndex each frame (opacity is constant, set in CSS)
        el.style.transform = `translate(-50%, -50%) translateX(${pos.toFixed(
          2
        )}px) translateZ(${z.toFixed(2)}px) rotateY(${ry.toFixed(
          2
        )}deg) scale(${sc.toFixed(3)})`;
        el.style.zIndex = String(Math.round(200 - Math.abs(pos)));
      });
    };

    const tick = () => {
      if (!dragging.current && !paused.current && !reduce) {
        offset.current = (offset.current - 0.5 + total) % total;
      }
      layout();
      raf = requestAnimationFrame(tick);
    };
    const start = () => {
      if (!running) {
        running = true;
        raf = requestAnimationFrame(tick);
      }
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(raf);
    };

    layout(); // position once even if never scrolled into view

    // only animate while the carousel is actually on screen (huge perf win:
    // 15 transformed cards no longer repaint every frame when scrolled away)
    const io = new IntersectionObserver(
      (entries) => (entries[0].isIntersecting ? start() : stop()),
      { rootMargin: "120px" }
    );
    io.observe(stage);

    return () => {
      io.disconnect();
      stop();
    };
  }, [slots]);

  const onDown = (e: React.PointerEvent) => {
    dragging.current = true;
    startX.current = e.clientX;
    startOffset.current = offset.current;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    offset.current = startOffset.current - (e.clientX - startX.current);
  };
  const onUp = () => {
    dragging.current = false;
  };

  return (
    <div
      className={styles.coverStage}
      ref={stageRef}
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
    >
      <div className={styles.coverTrack}>
        {Array.from({ length: slots }).map((_, i) => {
          const p = POINTS[i % N];
          return (
            <article
              key={i}
              ref={(el) => {
                cardRefs.current[i] = el;
              }}
              className={`${styles.glass} ${styles.coverCard}`}
              aria-hidden={i >= N ? true : undefined}
            >
              {/* Only the first N cards are real headings. This is an infinite
                  marquee, so it renders `slots` copies of the same 5 points,
                  which put 15 <h3>s in the prerendered homepage: each of
                  "First chat free", "100% personal", "Available 24/7",
                  "Private & secure" and "Real Vedic AI" appeared three times.
                  aria-hidden already handled screen readers, but it does NOT
                  stop Google reading duplicated headings out of the DOM.
                  The clones use a <div> instead; .title sets margin explicitly
                  (0 0 7px), so they render pixel-identical to the <h3>. */}
              {i < N ? (
                <h3 className={styles.title}>{p.title}</h3>
              ) : (
                <div className={styles.title}>{p.title}</div>
              )}
              <p className={styles.text}>{p.text}</p>
            </article>
          );
        })}
      </div>
    </div>
  );
}

export default function GlobalTrustPoints() {
  return (
    <section className={styles.section} aria-labelledby="trust-heading">
      <div className={styles.head}>
        <h2 id="trust-heading" className={styles.heading}>
          Why People Trust <span className={styles.accent}>Aura AI</span>
        </h2>
        <p className={styles.sub}>
          Ancient science, decoded with data-backed AI astrology.
        </p>
      </div>

      <CoverflowTrust />

    </section>
  );
}
